import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FolderOpen, Loader2, Search, Plus, Archive, ArchiveRestore, BookPlus, Eye, DoorOpen, Trash2, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useCoachClients } from "@/lib/coachQueries";
import { useCoachFilesWorkbench, useCreateLibraryItemFromFile } from "@/lib/coachWorkbenchQueries";
import { useCoachAuditImportsOverview } from "@/lib/auditImportQueries";
import { useArchiveFile, useUnarchiveFile, useDeleteFile, useCreateFileRoomLaunch, useFileUploadContext } from "@/lib/fileQueries";
import FileUploadDialog from "./files/FileUploadDialog";
import ImportPreviewDialog from "./files/ImportPreviewDialog";
import FilePreviewDrawer from "./files/FilePreviewDrawer";
import { SimpleDeleteConfirm } from "./shared/SimpleDeleteConfirm";
import { InteractiveFileBadge } from "./shared/InteractiveFileBadge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const SCOPE_LABEL = { workspace_shared: "Team", member_private: "Private", coach_private: "Coach only" };
const LIBRARY_CONTENT_TYPES = ["exercise", "framework", "template", "guide", "checklist"];
const AUDIT_STATUS_LABELS = { pending_review: "Awaiting review", accepted: "Accepted", partially_accepted: "Partially accepted", dismissed: "Dismissed" };

function AuditResultsBanner() {
  const { data } = useCoachAuditImportsOverview();
  const navigate = useNavigate();
  const items = data?.items || [];
  if (items.length === 0) return null;
  return (
    <div className="mb-4 border border-terracotta/30 bg-terracotta/5 rounded-lg p-4" data-testid="coach-files-workbench-audit-results-banner">
      <p className="text-xs uppercase tracking-wide text-terracotta font-medium mb-2">Audit Results</p>
      <ul className="space-y-1.5">
        {items.map((i) => (
          <li key={i.wai_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`coach-files-workbench-audit-result-${i.wai_id}`}>
            <span className="text-ink">{i.member_name} — <span className="text-ink-soft">{i.workspace_title}</span></span>
            <button
              onClick={() => navigate(`/rehearsal-room/coach/w/${i.workspace_id}/client/${i.member_id}/audit-result`)}
              className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${i.status === "pending_review" ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "bg-ink/10 text-ink-soft hover:bg-ink/20"}`}
              data-testid={`coach-files-workbench-audit-result-open-${i.wai_id}`}
            >
              {AUDIT_STATUS_LABELS[i.status] || i.status}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Master v5 Prompt 19 — cross-Workspace Files workbench: review/upload/
 * link/version/archive, with a clear split between "Upload to client"
 * (the file stays exactly where FileUploadDialog already puts it — one
 * Workspace, AudienceGate-scoped) and "Add to Library" (promotes an
 * ALREADY-uploaded file into a reusable, global LibraryItem — the file
 * itself never moves or gets copied, see programme_repository.
 * create_library_item_from_file).
 */
export default function CoachFilesWorkbenchPage() {
  const { data, isPending, isError } = useCoachFilesWorkbench();
  const { data: clientsData } = useCoachClients("all");
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [uploadWorkspaceId, setUploadWorkspaceId] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewFasId, setPreviewFasId] = useState(null);
  const [previewWorkspaceId, setPreviewWorkspaceId] = useState(null);
  const [libraryTarget, setLibraryTarget] = useState(null); // { fasId, workspaceId, filename }
  const [importReady, setImportReady] = useState(null); // { workspaceId, importId, draft }

  const workspaceOptions = useMemo(() => {
    const seen = new Map();
    for (const c of clientsData?.items || []) if (!seen.has(c.workspace_id)) seen.set(c.workspace_id, c.workspace_title);
    return Array.from(seen, ([workspace_id, workspace_title]) => ({ workspace_id, workspace_title }));
  }, [clientsData]);

  const { data: uploadContext } = useFileUploadContext(uploadWorkspaceId);
  const isUploadTeamWorkspace = uploadContext?.access_type === "team";
  const uploadMembers = useMemo(
    () => (clientsData?.items || []).filter((c) => c.workspace_id === uploadWorkspaceId).map((c) => ({ member_id: c.member_id, member_name: c.member_name })),
    [clientsData, uploadWorkspaceId]
  );
  const archiveFile = useArchiveFile(previewWorkspaceId || uploadWorkspaceId);
  const unarchiveFile = useUnarchiveFile(previewWorkspaceId || uploadWorkspaceId);
  const deleteFile = useDeleteFile(previewWorkspaceId || uploadWorkspaceId);
  const createRoomLaunch = useCreateFileRoomLaunch(previewWorkspaceId);
  const [deleteTarget, setDeleteTarget] = useState(null); // entry pending permanent delete

  const items = (data?.items || []).filter((e) => !query.trim() || e.asset.original_filename.toLowerCase().includes(query.trim().toLowerCase()));

  async function handleArchiveToggle(entry) {
    setPreviewWorkspaceId(entry.workspace_id);
    try {
      if (entry.asset.status === "archived") await unarchiveFile.mutateAsync(entry.asset.fas_id);
      else await archiveFile.mutateAsync(entry.asset.fas_id);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't update that file");
    }
  }

  async function handleConfirmDelete() {
    setPreviewWorkspaceId(deleteTarget.workspace_id);
    try {
      await deleteFile.mutateAsync(deleteTarget.asset.fas_id);
      toast.success(`"${deleteTarget.asset.original_filename}" was permanently deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't delete that file");
    }
  }

  async function handleOpenRoom(entry) {
    setPreviewWorkspaceId(entry.workspace_id);
    try {
      const res = await createRoomLaunch.mutateAsync(entry.asset.fas_id);
      navigate(`/rehearsal-room/w/${entry.workspace_id}/room/${res.launch_id}`);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't open this file in a Room");
    }
  }

  return (
    <div data-testid="coach-files-workbench-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-serif text-2xl text-ink flex items-center gap-2"><FolderOpen size={20} className="text-terracotta" /> Files</h1>
          <p className="text-sm text-ink-soft mt-1">Every file you can see, across every Workspace you coach.</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} data-testid="coach-files-workbench-upload-btn"><Plus size={15} className="mr-1.5" /> Upload to client</Button>
      </div>

      {uploadOpen && (
        <div className="mb-4 p-3 border border-ink/10 rounded-lg bg-white/50 max-w-md" data-testid="coach-files-workbench-upload-context">
          <p className="text-xs text-ink-soft mb-2">Choose which client/Workspace this upload is for:</p>
          <Select value={uploadWorkspaceId} onValueChange={setUploadWorkspaceId}>
            <SelectTrigger data-testid="coach-files-workbench-upload-workspace-select"><SelectValue placeholder="Select a Workspace…" /></SelectTrigger>
            <SelectContent>
              {workspaceOptions.map((w) => <SelectItem key={w.workspace_id} value={w.workspace_id}>{w.workspace_title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search files by name…" className="pl-8" data-testid="coach-files-workbench-search" />
      </div>

      <AuditResultsBanner />

      {isPending ? (
        <div className="flex items-center gap-2 text-ink-soft" data-testid="coach-files-workbench-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="coach-files-workbench-error">Couldn't load Files.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="coach-files-workbench-empty">No files match.</p>
      ) : (
        <div className="space-y-2" data-testid="coach-files-workbench-list">
          {items.map((entry) => (
            <div key={entry.asset.fas_id} className="border border-ink/10 bg-white/50 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`coach-files-workbench-row-${entry.asset.fas_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink truncate flex items-center gap-2">
                  {entry.asset.original_filename}
                  {entry.linked_work_documents?.length > 0 && <InteractiveFileBadge testId={`coach-files-workbench-interactive-badge-${entry.asset.fas_id}`} />}
                </p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[10px] text-ink-soft">{entry.member_name || "Team"} — {entry.workspace_title}</span>
                  <span className="text-[10px] uppercase tracking-wide text-terracotta bg-terracotta/10 rounded px-1.5 py-0.5">{SCOPE_LABEL[entry.scope] || entry.scope}</span>
                  {entry.asset.status === "archived" && <span className="text-[10px] uppercase tracking-wide text-ink-soft bg-ink/10 rounded px-1.5 py-0.5">Archived</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {entry.asset.status === "ready" && entry.linked_work_documents?.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button title="Open interactive version" data-testid={`coach-files-workbench-open-workdoc-${entry.asset.fas_id}`}><Sparkles size={14} className="text-terracotta hover:text-terracotta/70" /></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {entry.linked_work_documents.map((d) => (
                        <DropdownMenuItem key={d.wdoc_id} onClick={() => navigate(`/rehearsal-room/w/${entry.workspace_id}/work-documents/${d.wdoc_id}`)} data-testid={`coach-files-workbench-open-workdoc-item-${d.wdoc_id}`}>
                          {d.owner_member_name || d.title}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {entry.asset.status === "ready" && (entry.linked_work_documents?.length ?? 0) <= 1 && (
                  <button
                    title={entry.linked_work_documents?.length === 1 ? "Open interactive version" : "Preview"}
                    onClick={() => {
                      if (entry.linked_work_documents?.length === 1) navigate(`/rehearsal-room/w/${entry.workspace_id}/work-documents/${entry.linked_work_documents[0].wdoc_id}`);
                      else { setPreviewWorkspaceId(entry.workspace_id); setPreviewFasId(entry.asset.fas_id); }
                    }}
                    data-testid={`coach-files-workbench-preview-${entry.asset.fas_id}`}
                  >
                    {entry.linked_work_documents?.length === 1 ? <Sparkles size={14} className="text-terracotta hover:text-terracotta/70" /> : <Eye size={14} className="text-ink-soft hover:text-ink" />}
                  </button>
                )}
                {entry.task_linked && (
                  <button title="Open in Rehearsal Room" onClick={() => handleOpenRoom(entry)} data-testid={`coach-files-workbench-open-room-${entry.asset.fas_id}`}><DoorOpen size={14} className="text-ink-soft hover:text-terracotta" /></button>
                )}
                {entry.asset.status === "ready" && (
                  <button title="Add to Library" onClick={() => setLibraryTarget({ fasId: entry.asset.fas_id, workspaceId: entry.workspace_id, filename: entry.asset.original_filename })} data-testid={`coach-files-workbench-add-library-${entry.asset.fas_id}`}><BookPlus size={14} className="text-ink-soft hover:text-terracotta" /></button>
                )}
                {(entry.asset.status === "ready" || entry.asset.status === "archived") && (
                  <button title={entry.asset.status === "archived" ? "Unarchive" : "Archive"} onClick={() => handleArchiveToggle(entry)} data-testid={`coach-files-workbench-archive-toggle-${entry.asset.fas_id}`}>
                    {entry.asset.status === "archived" ? <ArchiveRestore size={14} className="text-ink-soft hover:text-ink" /> : <Archive size={14} className="text-ink-soft hover:text-ink" />}
                  </button>
                )}
                {entry.asset.status === "archived" && (
                  <button title="Delete permanently" onClick={() => setDeleteTarget(entry)} data-testid={`coach-files-workbench-delete-${entry.asset.fas_id}`}>
                    <Trash2 size={14} className="text-ink-soft hover:text-red-600" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadWorkspaceId && (
        <FileUploadDialog
          open={uploadOpen} onOpenChange={setUploadOpen} workspaceId={uploadWorkspaceId}
          actorRole="coach" isTeamWorkspace={isUploadTeamWorkspace} teamMembers={uploadContext?.team_members || []}
          onUploaded={() => setUploadOpen(false)}
          onImportReady={(payload) => { setUploadOpen(false); setImportReady({ workspaceId: uploadWorkspaceId, ...payload }); }}
        />
      )}
      {importReady && (
        <ImportPreviewDialog
          open={!!importReady} onOpenChange={(o) => !o && setImportReady(null)}
          workspaceId={importReady.workspaceId} importId={importReady.importId} initialDraft={importReady.draft}
          members={uploadMembers} isTeamWorkspace={isUploadTeamWorkspace}
        />
      )}
      {previewFasId && (
        <FilePreviewDrawer open={!!previewFasId} onOpenChange={(o) => !o && setPreviewFasId(null)} workspaceId={previewWorkspaceId} fasId={previewFasId} actorRole="coach" />
      )}
      <AddToLibraryDialog target={libraryTarget} onClose={() => setLibraryTarget(null)} />
      <SimpleDeleteConfirm
        open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this file permanently?"
        description={`"${deleteTarget?.asset.original_filename}" and its storage will be permanently removed. This can't be undone.`}
        onConfirm={handleConfirmDelete} isPending={deleteFile.isPending}
        testId="coach-files-workbench-delete-confirm"
      />
    </div>
  );
}

function AddToLibraryDialog({ target, onClose }) {
  const createFromFile = useCreateLibraryItemFromFile();
  const [title, setTitle] = useState("");
  const [contentType, setContentType] = useState("template");

  // Fix (testing agent, iteration 56, MEDIUM): the dialog stays mounted
  // with target=null between opens, so useState's initial value never
  // re-runs — sync explicitly whenever a new file is targeted.
  useEffect(() => {
    if (target) setTitle(target.filename);
  }, [target]);

  async function handleCreate() {
    if (!title.trim() || !target) return;
    try {
      await createFromFile.mutateAsync({ workspace_id: target.workspaceId, fas_id: target.fasId, content_type: contentType, title: title.trim() });
      toast.success("Added to Library as a draft — publish it from the Library workbench");
      onClose();
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't add that to Library");
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="coach-files-workbench-library-dialog">
        <DialogHeader><DialogTitle>Add to Library</DialogTitle></DialogHeader>
        <p className="text-xs text-ink-soft mb-2">Promotes "{target?.filename}" into a reusable Library item — the file itself stays exactly where it is.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-ink-soft mb-1 block">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="coach-files-workbench-library-title-input" />
          </div>
          <div>
            <label className="text-xs text-ink-soft mb-1 block">Content type</label>
            <Select value={contentType} onValueChange={setContentType}>
              <SelectTrigger data-testid="coach-files-workbench-library-content-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LIBRARY_CONTENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={handleCreate} disabled={!title.trim() || createFromFile.isPending} data-testid="coach-files-workbench-library-submit">
            {createFromFile.isPending ? "Adding…" : "Add to Library"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
